export default function AdSenseInfo() {
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <i className="ri-google-line"></i>
              <span>Programmatic Advertising</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Google AdSense Integration</h2>
            <p className="text-lg text-gray-600">
              In addition to direct ad sales, we utilize Google AdSense for programmatic advertising
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-lg">
                  <i className="ri-google-line text-3xl"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-1">Google AdSense</h3>
                  <p className="text-white/90">Automated ad optimization and revenue generation</p>
                </div>
              </div>
            </div>

            <div className="p-8">
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">How It Works</h4>
                  <p className="text-gray-600 leading-relaxed">
                    When direct ad slots are not occupied, we automatically fill them with Google AdSense ads. 
                    This ensures maximum revenue potential while maintaining a quality user experience. AdSense 
                    uses advanced algorithms to display relevant ads based on user behavior and content context.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-blue-50 rounded-lg p-6 border border-blue-100">
                    <div className="w-10 h-10 flex items-center justify-center bg-blue-600 text-white rounded-lg mb-4">
                      <i className="ri-funds-line text-xl"></i>
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Revenue Optimization</h4>
                    <p className="text-sm text-gray-600">
                      AdSense automatically optimizes ad placement and format to maximize revenue while 
                      maintaining user experience standards.
                    </p>
                  </div>

                  <div className="bg-emerald-50 rounded-lg p-6 border border-emerald-100">
                    <div className="w-10 h-10 flex items-center justify-center bg-emerald-600 text-white rounded-lg mb-4">
                      <i className="ri-shield-check-line text-xl"></i>
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Quality Control</h4>
                    <p className="text-sm text-gray-600">
                      All AdSense ads are vetted by Google to ensure they meet quality standards and 
                      are appropriate for our audience.
                    </p>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6 border border-purple-200">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-purple-600 text-white rounded-lg flex-shrink-0">
                      <i className="ri-information-line text-xl"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Direct Ads Take Priority</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        Direct ad purchases always take priority over AdSense placements. When you purchase 
                        a direct ad slot, AdSense ads are automatically disabled for that position, ensuring 
                        your ad gets 100% of the impressions for the duration of your campaign.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Current AdSense Performance</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-gray-900 mb-1">$1,200</div>
                      <div className="text-xs text-gray-600">Monthly AdSense Revenue</div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-gray-900 mb-1">85K</div>
                      <div className="text-xs text-gray-600">AdSense Impressions</div>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-gray-900 mb-1">1.4%</div>
                      <div className="text-xs text-gray-600">AdSense CTR</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Why Choose Direct Ads */}
          <div className="mt-8 bg-emerald-50 border border-emerald-200 rounded-lg p-6">
            <h4 className="font-semibold text-gray-900 mb-4">Why Choose Direct Ads Over AdSense?</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-emerald-600 text-xl mt-0.5"></i>
                <div>
                  <div className="font-medium text-gray-900 mb-1">Guaranteed Placement</div>
                  <div className="text-sm text-gray-600">Your ad shows 100% of the time, not competing with other advertisers</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-emerald-600 text-xl mt-0.5"></i>
                <div>
                  <div className="font-medium text-gray-900 mb-1">Brand Control</div>
                  <div className="text-sm text-gray-600">Complete control over your ad creative and messaging</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-emerald-600 text-xl mt-0.5"></i>
                <div>
                  <div className="font-medium text-gray-900 mb-1">Better Analytics</div>
                  <div className="text-sm text-gray-600">Detailed performance metrics and conversion tracking</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-line text-emerald-600 text-xl mt-0.5"></i>
                <div>
                  <div className="font-medium text-gray-900 mb-1">Local Focus</div>
                  <div className="text-sm text-gray-600">Reach specifically targeted Roseville residents and visitors</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}